// 마케팅 가입 전환 트래킹 (ALLIBEE-835)
// GTM 컨테이너를 거치지 않고 구글 애즈 gtag·네이버 프리미엄 로그분석(wcs)·메타 픽셀(ALLIBEE-1241)을 직접 삽입한다 — 마케팅 요청 사항.

import { getFirstAdTouchFeature, getFirstTouchFeature } from '@allibee/utils';

const GOOGLE_ADS_ID = 'AW-16576590778';
const NAVER_WCS_ID = 's_941bcb0c2ea';
const META_PIXEL_ID = '1675555740122792';
// cue/app 어느 도메인에서든 유입 도메인을 allibee.ai 로 고정 — 도메인 이동 시 네이버 쿠키 유실로 전환이 0 집계되는 것을 방지.
const NAVER_INFLOW_DOMAIN = 'allibee.ai';

const SIGNUP_CONVERSIONS = {
    CUE: { googleSendTo: `${GOOGLE_ADS_ID}/9YrjCPnZ5uccELrnquA9`, naverType: 'custom001' },
    AGENT: { googleSendTo: `${GOOGLE_ADS_ID}/LpUfCKWxh-gcELrnquA9`, naverType: 'custom002' },
} as const;

export type SignupConversionService = keyof typeof SIGNUP_CONVERSIONS;

interface NaverWcs {
    inflow: (domain?: string) => void;
    trans: (params: { type: string }) => void;
}

// 메타 픽셀 기본 코드의 fbq 스텁 — fbevents.js 로드 전 호출은 queue 에 쌓였다가 로드 후 callMethod 로 재생된다
interface MetaPixel {
    (...args: unknown[]): void;
    callMethod?: (...args: unknown[]) => void;
    queue: IArguments[];
    push?: MetaPixel;
    loaded?: boolean;
    version?: string;
}

export type ConversionTrackingWindow = Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    wcs?: NaverWcs;
    wcs_add?: Record<string, string>;
    wcs_do?: () => void;
    // wcs 스크립트 로드 전에 발생한 전환을 보관하는 큐 — gtag 는 dataLayer 가 큐 역할을 하지만 wcs 는 자체 큐가 없다
    bhsnPendingNaverConversions?: string[];
    fbq?: MetaPixel;
    _fbq?: MetaPixel;
};

const GTAG_SCRIPT_ID = 'bhsn-gtag-script';
const WCS_SCRIPT_ID = 'bhsn-wcs-script';
const META_PIXEL_SCRIPT_ID = 'bhsn-meta-pixel-script';

function getWindow(): ConversionTrackingWindow | undefined {
    return (globalThis as typeof globalThis & { window?: ConversionTrackingWindow }).window;
}

// cue 와 contract(MFE)가 한 문서를 공유할 수 있어, 번들별 모듈 플래그가 아닌 DOM script id 로 중복 삽입을 막는다.
export function initConversionTracking(enabled: boolean) {
    const browserWindow = getWindow();
    if (!enabled || browserWindow === undefined) return;

    const inject = () => {
        const doc = browserWindow.document;

        if (!doc.getElementById(GTAG_SCRIPT_ID)) {
            browserWindow.dataLayer ??= [];
            browserWindow.gtag ??= function gtag() {
                // gtag.js 는 arguments 객체 자체를 큐로 해석한다 — rest 파라미터 배열로 바꾸면 동작하지 않는다.
                // eslint-disable-next-line prefer-rest-params
                browserWindow.dataLayer?.push(arguments);
            };
            browserWindow.gtag('js', new Date());
            browserWindow.gtag('config', GOOGLE_ADS_ID);

            const script = doc.createElement('script');
            script.id = GTAG_SCRIPT_ID;
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
            doc.head.appendChild(script);
        }

        if (!doc.getElementById(WCS_SCRIPT_ID)) {
            const script = doc.createElement('script');
            script.id = WCS_SCRIPT_ID;
            script.async = true;
            script.src = 'https://wcs.naver.net/wcslog.js';
            script.onload = () => {
                browserWindow.wcs_add ??= {};
                browserWindow.wcs_add['wa'] = NAVER_WCS_ID;
                browserWindow.wcs?.inflow(NAVER_INFLOW_DOMAIN);
                browserWindow.wcs_do?.();

                const pending = browserWindow.bhsnPendingNaverConversions;
                if (pending?.length && browserWindow.wcs) {
                    pending.forEach(type => browserWindow.wcs?.trans({ type }));
                }
                browserWindow.bhsnPendingNaverConversions = [];
            };
            doc.head.appendChild(script);
        }

        if (!doc.getElementById(META_PIXEL_SCRIPT_ID)) {
            if (!browserWindow.fbq) {
                const fbq: MetaPixel = Object.assign(
                    function fbq() {
                        // 메타 기본 코드와 같이 arguments 객체를 그대로 큐에 넣는다 — fbevents.js 가 이 형태로 재생한다.
                        // eslint-disable-next-line prefer-rest-params
                        if (fbq.callMethod) fbq.callMethod(...Array.from(arguments));
                        // eslint-disable-next-line prefer-rest-params
                        else fbq.queue.push(arguments);
                    },
                    { queue: [] as IArguments[], loaded: true, version: '2.0' },
                );
                fbq.push = fbq;
                browserWindow.fbq = fbq;
                browserWindow._fbq ??= fbq;
            }
            browserWindow.fbq('init', META_PIXEL_ID);
            browserWindow.fbq('track', 'PageView');

            const script = doc.createElement('script');
            script.id = META_PIXEL_SCRIPT_ID;
            script.async = true;
            script.src = 'https://connect.facebook.net/en_US/fbevents.js';
            doc.head.appendChild(script);
        }
    };

    // svelte:head 즉시 삽입은 LCP 이전 대역폭을 잠식한다 — GTM 로더와 같은 이유로 idle 시점으로 미룬다.
    if ('requestIdleCallback' in browserWindow) {
        browserWindow.requestIdleCallback(inject, { timeout: 4000 });
    } else {
        setTimeout(inject, 1);
    }
}

// 가입 성공 API 응답 직후에만 호출한다 — 완료 화면 렌더링 등 새로고침으로 재실행되는 경로에서 호출하면 중복 발화된다.
// initConversionTracking 이 실행되지 않은 환경(dev·온프레미스 테마)에서는 gtag/wcs/fbq 가 없어 자연히 no-op.
export function trackSignupConversion(service: SignupConversionService) {
    const browserWindow = getWindow();
    if (browserWindow === undefined) return;

    const { googleSendTo, naverType } = SIGNUP_CONVERSIONS[service];
    // first_touch_feature(ALLIBEE-1173)·first_ad_touch_feature(ALLIBEE-1190)는 구글 애즈 전환 맞춤 변수로 매핑되므로 키 이름을 바꾸면 집계가 끊긴다.
    // 가입 직후 전체 페이지 이동이 있어도 전송이 끊기지 않도록 beacon 전송
    browserWindow.gtag?.('event', 'conversion', {
        send_to: googleSendTo,
        first_touch_feature: getFirstTouchFeature(),
        first_ad_touch_feature: getFirstAdTouchFeature(),
        transport_type: 'beacon',
    });
    if (browserWindow.wcs && browserWindow.wcs_add) {
        browserWindow.wcs.trans({ type: naverType });
    } else {
        (browserWindow.bhsnPendingNaverConversions ??= []).push(naverType);
    }
    browserWindow.fbq?.('track', 'CompleteRegistration');
}
